import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  TrophyIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

interface ScorerDashboardHomeProps {
  tournamentId: string;
}

interface LeaderboardEntry {
  player_name: string;
  category: string;
  total_score: number;
  position: number;
  rounds_played: number;
}

interface ScoringStats {
  totalGroups: number;
  playersScored: number;
  totalPlayers: number;
  roundNumber: number;
  roundId: string;
}

export default function ScorerDashboardHome({ tournamentId }: ScorerDashboardHomeProps) {
  const [userName, setUserName] = useState('');
  const [tournamentName, setTournamentName] = useState('');
  const [stats, setStats] = useState<ScoringStats>({ totalGroups: 0, playersScored: 0, totalPlayers: 0, roundNumber: 1, roundId: '' });
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: authUser } = await supabase
          .from('auth_users')
          .select('full_name')
          .eq('id', user.id)
          .maybeSingle();
        setUserName(authUser?.full_name || user.email?.split('@')[0] || 'Scorer');
      }

      if (!tournamentId) return;

      const { data: tournament } = await supabase
        .from('tournaments')
        .select('name')
        .eq('id', tournamentId)
        .maybeSingle();

      if (tournament) setTournamentName(tournament.name);

      const { data: rounds } = await supabase
        .from('tournament_rounds')
        .select('id, round_number')
        .eq('tournament_id', tournamentId)
        .order('round_number', { ascending: false })
        .limit(1);

      if (rounds && rounds.length > 0) {
        const roundId = rounds[0].id;
        const roundNumber = rounds[0].round_number;

        const { data: pairings } = await supabase
          .from('player_pairings')
          .select('group_number, player_id')
          .eq('round_id', roundId);

        const groupNumbers = new Set(pairings?.map(p => p.group_number) || []);
        const pairedPlayerIds = new Set(pairings?.map(p => p.player_id) || []);

        const { data: holeScores } = await supabase
          .from('hole_scores')
          .select('player_id')
          .eq('tournament_id', tournamentId)
          .eq('round', roundNumber);

        const scoredPlayers = new Set(holeScores?.map(h => h.player_id) || []);

        setStats({
          totalGroups: groupNumbers.size,
          playersScored: scoredPlayers.size,
          totalPlayers: pairedPlayerIds.size,
          roundNumber,
          roundId,
        });
      }

      const { data: leaderboardData } = await supabase
        .from('leaderboard')
        .select('player_name, category, total_score, position, rounds_played')
        .eq('tournament_id', tournamentId)
        .order('position', { ascending: true })
        .limit(10);

      if (leaderboardData) {
        setLeaderboard(leaderboardData);
      }
    } catch (err) {
      console.error('Error fetching scorer dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const formatScore = (score: number): string => {
    if (score === 0) return 'E';
    if (score >= 80) return '--';
    return score > 0 ? `+${score}` : score.toString();
  };

  const completionPercent = stats.totalPlayers > 0
    ? Math.round((stats.playersScored / stats.totalPlayers) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-lg"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Scorer Portal</p>
            <h1 className="text-2xl font-bold">Welcome, {userName}</h1>
            {tournamentName && (
              <p className="text-sm text-gray-400 mt-1">{tournamentName}</p>
            )}
          </div>
          <div className="hidden sm:block">
            <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center">
              <UserCircleIcon className="w-8 h-8 text-white/80" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scoring Stats + Enter Scores CTA */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="bg-orange-50 p-2.5 rounded-lg">
              <ClipboardDocumentListIcon className="w-5 h-5 text-golf-orange" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Scoring Progress</h2>
              <p className="text-xs text-gray-500">Round {stats.roundNumber}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-5">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.totalGroups}</p>
              <p className="text-xs text-gray-500 mt-1">Groups</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.playersScored}</p>
              <p className="text-xs text-gray-500 mt-1">Players Scored</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.totalPlayers}</p>
              <p className="text-xs text-gray-500 mt-1">Total Players</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="text-gray-600 font-medium">Completion</span>
              <span className="text-gray-900 font-semibold">{completionPercent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="h-2.5 rounded-full transition-all duration-500 bg-golf-orange"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </div>
        </motion.div>

        {/* Enter Scores CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="flex flex-col gap-4"
        >
          <button
            onClick={() => navigate('/scorer/groups')}
            className="flex-1 bg-golf-orange hover:bg-golf-orange/90 rounded-xl p-6 flex flex-col items-center justify-center gap-3 transition-colors text-center shadow-sm"
          >
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <ClipboardDocumentListIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="font-bold text-white text-base">Enter Scores</p>
              <p className="text-sm text-white/80 mt-0.5">Open scorecard entry</p>
            </div>
          </button>

          <button
            onClick={handleSignOut}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-red-100 bg-red-50 hover:bg-red-100 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4 text-red-500" />
            <span className="text-sm font-medium text-red-600">Sign Out</span>
          </button>
        </motion.div>
      </div>

      {/* Leaderboard */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-amber-50 p-2 rounded-lg">
              <TrophyIcon className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Leaderboard</h2>
          </div>
          <button
            onClick={() => navigate('/leaderboard')}
            className="text-sm text-golf-orange font-medium hover:underline flex items-center gap-1"
          >
            View Full
            <ChevronRightIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {leaderboard.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">Pos</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Player</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Rounds</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leaderboard.map((entry, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                        entry.position === 1 ? 'bg-amber-100 text-amber-700' :
                        entry.position === 2 ? 'bg-gray-200 text-gray-700' :
                        entry.position === 3 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {entry.position}
                      </span>
                    </td>
                    <td className="px-6 py-3 font-medium text-gray-900">{entry.player_name}</td>
                    <td className="px-6 py-3 text-gray-500">{entry.category || '-'}</td>
                    <td className="px-6 py-3 text-gray-500 text-right">{entry.rounds_played}</td>
                    <td className="px-6 py-3 text-right">
                      <span className={`font-semibold ${
                        entry.total_score < 0 ? 'text-red-600' :
                        entry.total_score === 0 ? 'text-gray-900' :
                        'text-gray-700'
                      }`}>
                        {formatScore(entry.total_score)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <TrophyIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No leaderboard data available yet</p>
            <p className="text-xs text-gray-400 mt-1">Scores will appear here once entered</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
